import { useEffect, useState, useCallback } from "react";
import { Plus, Trash, PencilSimple, Pause, Play, ArrowDownLeft, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = { type: "payable", amount: "", currency: "TRY", contact_id: "", contact_name: "", account_id: "", description: "", frequency: "monthly", next_run: "", active: true };
const FREQ = { monthly: "Aylık", weekly: "Haftalık" };
const NONE = "__none__";

export default function Recurring() {
  const { currencies } = useCurrency();
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get("/recurring").then((r) => setItems(r.data)).catch(() => {});
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickContact = (v) => {
    if (v === NONE) return setForm((f) => ({ ...f, contact_id: "", contact_name: "" }));
    const c = contacts.find((x) => x.id === v);
    setForm((f) => ({ ...f, contact_id: v, contact_name: c ? c.name : "" }));
  };
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ ...r, contact_id: r.contact_id || "", account_id: r.account_id || "", amount: String(r.amount), next_run: (r.next_run || "").slice(0, 10) });
    setEditId(r.id);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.amount || !form.next_run) return toast.error("Tutar ve başlangıç tarihi girin");
    const payload = { ...form, amount: Number(form.amount), contact_id: form.contact_id || null, account_id: form.account_id || null, next_run: new Date(form.next_run).toISOString() };
    try {
      if (editId) await api.put(`/recurring/${editId}`, payload);
      else await api.post("/recurring", payload);
      toast.success(editId ? "Güncellendi" : "Sabit ödeme eklendi");
      setOpen(false);
      load();
    } catch (e) { toast.error("Kaydedilemedi"); }
  };

  const toggle = async (id) => { await api.post(`/recurring/${id}/toggle`); load(); };
  const remove = async (id) => { await api.delete(`/recurring/${id}`); toast.success("Silindi"); load(); };

  const pickAccount = (v) => set("account_id", v === NONE ? "" : v);

  // Aktif sabit kayıtların para birimine göre toplamları
  const totals = items.reduce((acc, r) => {
    if (!r.active) return acc;
    const cur = r.currency || "TRY";
    acc[r.type] = acc[r.type] || {};
    acc[r.type][cur] = (acc[r.type][cur] || 0) + Number(r.amount || 0);
    return acc;
  }, {});
  const payableTotals = Object.entries(totals.payable || {});
  const receivableTotals = Object.entries(totals.receivable || {});

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light tracking-tight">Sabit Ödemeler</h1>
          <p className="text-sm text-muted-foreground mt-1">Kira, maaş gibi düzenli ödeme ve tahsilatlar otomatik olarak vadesinde oluşturulur</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-recurring-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus size={18} className="mr-1" /> Yeni Sabit Kayıt
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-heading">{editId ? "Düzenle" : "Yeni Sabit Ödeme / Tahsilat"}</DialogTitle>
              <DialogDescription className="text-muted-foreground">Düzenli tekrarlanan ödeme veya tahsilatı tanımlayın; vadesinde otomatik oluşturulur.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tür</Label>
                  <Select value={form.type} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="rec-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="payable">Ödeme (Gider)</SelectItem>
                      <SelectItem value="receivable">Tahsilat (Gelir)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Sıklık</Label>
                  <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="rec-frequency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Aylık</SelectItem>
                      <SelectItem value="weekly">Haftalık</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tutar</Label>
                  <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="rec-amount" placeholder="0" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Para Birimi</Label>
                  <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="rec-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Sonraki Tarih</Label>
                  <Input type="date" value={form.next_run} onChange={(e) => set("next_run", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="rec-next" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Cari Hesap</Label>
                  <Select value={form.contact_id || NONE} onValueChange={pickContact}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="rec-contact"><SelectValue placeholder="Cari seçin" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— Cari yok —</SelectItem>
                      {contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Banka Hesabı</Label>
                <Select value={form.account_id || NONE} onValueChange={pickAccount}>
                  <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="rec-account"><SelectValue placeholder="Hesap seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>— Hesap yok —</SelectItem>
                    {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">Ödeme kapatıldığında tutar bu hesaba işlenir</p>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Açıklama</Label>
                <Input value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="rec-description" placeholder="Örn. Ofis kirası" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} data-testid="rec-save" className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="recurring-totals">
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <ArrowUpRight size={18} className="text-[#EF4444]" />
            <h2 className="text-sm font-medium">Toplam Sabit Gider (Aktif)</h2>
          </div>
          {payableTotals.length === 0 ? (
            <p className="text-xs text-muted-foreground">Kayıt yok.</p>
          ) : (
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              {payableTotals.map(([cur, amt]) => (
                <div key={cur} data-testid={`recurring-total-payable-${cur}`} className="font-heading text-xl font-semibold font-mono-num text-[#EF4444]">
                  {formatMoney(amt, cur, currencies)}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-card border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <ArrowDownLeft size={18} className="text-[#10B981]" />
            <h2 className="text-sm font-medium">Toplam Sabit Tahsilat (Aktif)</h2>
          </div>
          {receivableTotals.length === 0 ? (
            <p className="text-xs text-muted-foreground">Kayıt yok.</p>
          ) : (
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              {receivableTotals.map(([cur, amt]) => (
                <div key={cur} data-testid={`recurring-total-receivable-${cur}`} className="font-heading text-xl font-semibold font-mono-num text-[#10B981]">
                  {formatMoney(amt, cur, currencies)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <p className="text-sm text-muted-foreground">Henüz sabit ödeme tanımlanmadı.</p>}
        {items.map((r) => (
          <div key={r.id} data-testid={`recurring-card-${r.id}`}
            className={`bg-card border rounded-lg p-6 transition-transform hover:-translate-y-1 ${r.active ? "border-border" : "border-border opacity-60"}`}>
            <div className="flex items-start justify-between">
              <div className={`h-10 w-10 rounded-md flex items-center justify-center ${r.type === "receivable" ? "bg-success/10" : "bg-destructive/10"}`}>
                {r.type === "receivable" ? <ArrowDownLeft size={20} className="text-[#10B981]" /> : <ArrowUpRight size={20} className="text-[#EF4444]" />}
              </div>
              <div className="flex gap-1">
                <button onClick={() => toggle(r.id)} data-testid={`recurring-toggle-${r.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1" title={r.active ? "Duraklat" : "Etkinleştir"}>
                  {r.active ? <Pause size={16} /> : <Play size={16} />}
                </button>
                <button onClick={() => openEdit(r)} data-testid={`recurring-edit-${r.id}`} className="text-muted-foreground hover:text-primary transition-colors p-1" title="Düzenle"><PencilSimple size={16} /></button>
                <ConfirmDelete onConfirm={() => remove(r.id)} testId={`recurring-delete-${r.id}`}
                  title="Sabit kaydı sil?" description="Bu sabit ödeme/tahsilat tanımı silinecek. Bu işlem geri alınamaz.">
                  <Trash size={16} />
                </ConfirmDelete>
              </div>
            </div>
            <div className="mt-4 text-sm font-medium">{r.contact_name || r.description || "Sabit Kayıt"}</div>
            <div className="text-xs text-muted-foreground">{r.description}</div>
            <div className={`mt-3 font-heading text-xl font-semibold font-mono-num ${r.type === "receivable" ? "text-[#10B981]" : "text-[#EF4444]"}`}>
              {formatMoney(r.amount, r.currency, currencies)}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <Badge variant="outline" className="border-primary/40 text-primary text-[10px]">{FREQ[r.frequency] || r.frequency}</Badge>
              <span className="text-xs text-muted-foreground">Sonraki: {formatDate(r.next_run)}</span>
              {!r.active && <Badge variant="outline" className="border-border text-muted-foreground text-[10px]">Duraklatıldı</Badge>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
